import React, { useState } from "react";
import PropTypes from "prop-types";
import { Divider, Icon } from "orcs-design-system";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Link } from "react-router-dom";
import {
  useUserPerson,
  useIsContextReady,
} from "src/contexts/global/GlobalContext";
import { useEnableDarkThemeSwitcherFlag } from "src/hooks/useEnableDarkThemeSwitcherFlag";
import AvatarModal from "src/commonComponents/ProfileHeader/AvatarModal";
import ThemeSwitchToggle from "src/components/Nav/ThemeSwitchToggle";
import PATHS, { url } from "src/consts/urlPaths";

const MenuButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: ${themeGet("radii.2")};
  gap: ${themeGet("space.s")};
  padding: ${themeGet("space.xs")} ${themeGet("space.s")};
  font-size: ${themeGet("fontSizes.1")};
  cursor: pointer;
  color: ${themeGet("colors.bodyText")};
  font-family: ${themeGet("fonts.main")};
  font-weight: ${themeGet("fontWeights.2")};
  &:hover,
  &:focus {
    background-color: ${themeGet("colors.primaryLightest")};
  }
`;

const MenuLink = styled(Link)`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: ${themeGet("radii.2")};
  gap: ${themeGet("space.s")};
  padding: ${themeGet("space.xs")} ${themeGet("space.s")};
  font-size: ${themeGet("fontSizes.1")};
  cursor: pointer;
  color: ${themeGet("colors.bodyText")};
  font-family: ${themeGet("fonts.main")};
  font-weight: ${themeGet("fontWeights.2")};
  text-decoration: none;
  &:hover,
  &:focus {
    background-color: ${themeGet("colors.primaryLightest")};
  }
`;

const LogoutButton = MenuButton;

const UserMenuContent = ({ themeSwitcher, logout }) => {
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const userPerson = useUserPerson();
  const isContextReady = useIsContextReady();
  const enableDarkThemeSwitcher = useEnableDarkThemeSwitcherFlag();
  // Check if user exists and has an aggregateId (indicating they're a valid person in the system)
  const isUserMemberOfWorkspace = userPerson && userPerson.aggregateId;

  // Show profile upload when context is ready and user is in workspace
  const shouldShowProfileUpload = isContextReady && isUserMemberOfWorkspace;

  const handleProfilePictureClick = () => {
    setShowAvatarModal(true);
  };

  const handleAvatarModalClose = () => {
    setShowAvatarModal(false);
  };

  return (
    <>
      {shouldShowProfileUpload && (
        <>
          <LogoutButton
            aria-label="Upload profile picture"
            onClick={handleProfilePictureClick}
          >
            <Icon icon={["fas", "user"]} />
            Upload Profile Picture
          </LogoutButton>
          <Divider light />
        </>
      )}

      {enableDarkThemeSwitcher && (
        <>
          <ThemeSwitchToggle />
          <Divider light />
        </>
      )}
      {/* TODO: Menu doesn't close when clicking this link. Need to add onMenuItemClick
          prop to ORCS UserMenu component to properly close menu on navigation.
          Track in ORCS design system backlog. */}
      <MenuLink to={url(PATHS.LOGIN_HISTORY_PAGE)} aria-label="Login History">
        <Icon icon={["far", "history"]} />
        Login History
      </MenuLink>
      <Divider light />
      <LogoutButton aria-label="Logout" onClick={logout}>
        <Icon icon={["fas", "lock"]} />
        Logout
      </LogoutButton>

      {showAvatarModal && (
        <AvatarModal
          isOpen={showAvatarModal}
          onClose={handleAvatarModalClose}
          personId={userPerson?.aggregateId}
        />
      )}
    </>
  );
};

UserMenuContent.propTypes = {
  themeSwitcher: PropTypes.func,
  logout: PropTypes.func,
};

export default UserMenuContent;
